import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { AnimatePresence, motion } from 'framer-motion';
import { getIsSearchBoxActive } from 'modules/searchBox/selectors';
import { closeSearchBox, setIsSearchBoxActive } from 'modules/searchBox/slice';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { useBodyScrollLock } from '@daedalus/atlas/Overlay/useBodyScrollLock';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
interface Props {
  /** Content to render under the shadow overlay */
  children: ReactNode;
}
interface ShadowProps {
  /** The dataId applied to the shadow overlay */
  dataId: string;
  /** The click handler */
  onClick: () => void;
  /** The `touchmove` handler. When "active" is set to false, the event listener is destroyed. */
  onTouchMove?: () => void;
  /** Whether the shadow is visible */
  isVisible: boolean;
}
const ANIMATION_DURATION = 0.2;
const _exp = /*#__PURE__*/() => Flex.Column;
const Wrapper = /*#__PURE__*/styled(_exp())({
  name: "Wrapper",
  class: "w1ua1obx",
  propsAsIs: true
});
const _exp2 = /*#__PURE__*/() => motion.div;
const ShadowElement = /*#__PURE__*/styled(_exp2())({
  name: "ShadowElement",
  class: "s1kvj50i",
  propsAsIs: true
});
const Shadow = React.forwardRef(({
  dataId,
  isVisible,
  onClick,
  onTouchMove
}: ShadowProps, ref: React.MutableRefObject<HTMLDivElement>) => {
  useEffect(() => {
    if (onTouchMove && isVisible && ref.current) {
      ref.current.addEventListener('touchmove', onTouchMove);
    }
    return () => {
      if (onTouchMove && isVisible && ref.current) {
        ref.current.removeEventListener('touchmove', onTouchMove);
      }
    };
  }, [isVisible, onTouchMove, ref]);
  return <AnimatePresence>
        {isVisible && <ShadowElement ref={ref} key="ShadowElement" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }} data-id={dataId} onClick={onClick} />}
      </AnimatePresence>;
});
Shadow.displayName = 'Shadow';
const SearchBoxShadow = ({
  children
}: Props) => {
  const dispatch = useDispatch();
  const {
    isMobile
  } = useDeviceLayout();
  const isShadowVisible = useSelector(getIsSearchBoxActive);
  const ref = useRef<HTMLDivElement>();
  useBodyScrollLock({
    ref,
    isEnabled: isShadowVisible && !isMobile
  });
  const closeOverlay = useCallback(() => {
    dispatch(setIsSearchBoxActive(false));
    dispatch(closeSearchBox());
  }, [dispatch]);
  const handleClick = useCallback(() => {
    if (isMobile) {
      closeOverlay();
    }
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.SearchBoxShadow,
      action: Action.Clicked
    }));
  }, [isMobile, closeOverlay, dispatch]);
  const onTouchMove = useCallback(() => {
    if (isMobile) {
      closeOverlay();
    }
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.SearchBoxShadow,
      action: Action.Scrolled
    }));
  }, [isMobile, closeOverlay, dispatch]);
  return <Wrapper>
      <Shadow ref={ref} isVisible={isShadowVisible} dataId="SearchBoxShadow" onClick={handleClick} onTouchMove={onTouchMove} />
      {children}
    </Wrapper>;
};
export default SearchBoxShadow;

require("./index.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");